import {
  PublishErrorCode,
  PublishErrorCodeType,
  ErrorCategory,
  ErrorCodeToCategory,
  ErrorCategoryType,
} from '../constants/errorCodes';

// Retry configuration from server/src/index.ts
const RETRY_CONFIG = {
  maxAttempts: 5,
  minBackoff: 300, // 5 minutes in seconds
  maxBackoff: 3600, // 1 hour in seconds
  maxDoublings: 5,
};

export type ErrorActionType =
  | 'edit'
  | 'report'
  | 'retry'
  | 'cancel'
  | 'reconnect';

export interface ErrorAction {
  type: ErrorActionType;
  label: string;
  variant: 'default' | 'outline' | 'destructive' | 'secondary';
  icon?: string;
}

/**
 * Get a user-friendly error message based on the error code
 */
export function getErrorMessage(
  code: string,
  message: string | undefined,
  platform: string,
  retryCount: number,
  retryable?: boolean,
  accountReconnected?: boolean
): string {
  const errorCode = code as PublishErrorCodeType;
  const category = ErrorCodeToCategory[errorCode] || ErrorCategory.SYSTEM;

  switch (category) {
    case ErrorCategory.AUTH:
      if (accountReconnected) {
        return `This post didn't publish because ${platform} needed reconnecting. Your account is connected again, so you can retry it now.`;
      }
      if (code === PublishErrorCode.AUTH_NO_REFRESH_TOKEN) {
        return `Your ${platform} connection has expired. Please reconnect your account.`;
      }
      if (code === PublishErrorCode.AUTH_REFRESH_FAILED) {
        return `Failed to refresh ${platform} access. Please reconnect your account.`;
      }
      if (code === PublishErrorCode.AUTH_PERMISSION_MISSING) {
        return `Your ${platform} connection is missing publishing permission. Please reconnect your account and approve all requested permissions.`;
      }
      return `Your ${platform} connection has expired. Please reconnect your account.`;

    case ErrorCategory.RATE_LIMIT:
      if (retryable === false) {
        return message || `${platform} rate limited. Please wait before trying again.`;
      }
      return `${platform} rate limited. Will retry automatically.`;

    case ErrorCategory.VALIDATION:
      // Use the specific message if available
      if (message) {
        return message;
      }
      if (code === PublishErrorCode.UNSUPPORTED_MEDIA) {
        return `${platform} doesn't support this media type. Please edit your post.`;
      }
      if (code === PublishErrorCode.CONTENT_POLICY) {
        return `Your content may violate ${platform}'s content policy.`;
      }
      return `Your post doesn't meet ${platform}'s requirements. Please edit your post.`;

    case ErrorCategory.SYSTEM:
      if (retryable === false) {
        return message || `${platform} publishing failed. Please try again or report this issue.`;
      }
      if (retryCount >= RETRY_CONFIG.maxAttempts) {
        return `Failed after ${retryCount} attempts. Please try again or report this issue.`;
      }
      return message || `A system error occurred. Will retry automatically.`;

    default:
      return message || 'An unknown error occurred.';
  }
}

/**
 * Get the error category from error code
 */
export function getErrorCategory(code: string): ErrorCategoryType {
  return ErrorCodeToCategory[code as PublishErrorCodeType] || ErrorCategory.SYSTEM;
}

/**
 * Get the appropriate action buttons for an error based on its category
 */
export function getErrorActions(
  code: string,
  retryCount: number,
  cancelled?: boolean,
  hasPublishedConnections?: boolean,
  retryable?: boolean,
  accountReconnected?: boolean
): ErrorAction[] {
  const category = getErrorCategory(code);
  const actions: ErrorAction[] = [];

  // Media retrieval failures need another publishing attempt, not an edit.
  if (code === PublishErrorCode.MEDIA_DOWNLOAD_FAILED) {
    return [
      { type: 'retry', label: 'Retry', variant: 'default' },
      { type: 'report', label: 'Report Issue', variant: 'outline' },
    ];
  }

  switch (category) {
    case ErrorCategory.AUTH:
      // An auth failure flips the connection to "expired"; once it's active
      // again the user has reconnected, and the failed post can go out as-is.
      if (accountReconnected) {
        actions.push({
          type: 'retry',
          label: 'Retry',
          variant: 'default',
        });
        actions.push({
          type: 'report',
          label: 'Report Issue',
          variant: 'outline',
        });
        break;
      }
      actions.push({
        type: 'reconnect',
        label: 'Reconnect Account',
        variant: 'default',
      });
      actions.push({
        type: 'report',
        label: 'Report Issue',
        variant: 'outline',
      });
      break;

    case ErrorCategory.RATE_LIMIT:
      if (retryable === false) {
        actions.push({
          type: 'retry',
          label: 'Try Again',
          variant: 'default',
        });
        actions.push({
          type: 'report',
          label: 'Report Issue',
          variant: 'outline',
        });
      } else if (!cancelled && retryCount < RETRY_CONFIG.maxAttempts) {
        // Active automatic rate-limit retry; let the user cancel it.
        actions.push({
          type: 'cancel',
          label: 'Cancel Retry',
          variant: 'outline',
        });
      }
      break;

    case ErrorCategory.VALIDATION:
      if (hasPublishedConnections) {
        // Can't edit if some platforms already published — offer retry and report instead
        actions.push({
          type: 'retry',
          label: 'Try Again',
          variant: 'default',
        });
        actions.push({
          type: 'report',
          label: 'Report Issue',
          variant: 'outline',
        });
      } else {
        actions.push({
          type: 'edit',
          label: 'Edit Post',
          variant: 'default',
        });
      }
      break;

    case ErrorCategory.SYSTEM:
      if (
        retryable === false ||
        retryCount >= RETRY_CONFIG.maxAttempts ||
        cancelled
      ) {
        // Max retries reached or cancelled - show report and retry options
        actions.push({
          type: 'retry',
          label: 'Try Again',
          variant: 'default',
        });
        actions.push({
          type: 'report',
          label: 'Report Issue',
          variant: 'outline',
        });
      } else {
        // Still retrying - show cancel option
        actions.push({
          type: 'cancel',
          label: 'Cancel Retry',
          variant: 'outline',
        });
      }
      break;

    default:
      // Unknown errors - show report option
      if (retryCount >= RETRY_CONFIG.maxAttempts || cancelled) {
        actions.push({
          type: 'retry',
          label: 'Try Again',
          variant: 'default',
        });
        actions.push({
          type: 'report',
          label: 'Report Issue',
          variant: 'outline',
        });
      }
      break;
  }

  return actions;
}

/**
 * Check if the error should show an auto-retry indicator
 */
export function shouldShowAutoRetryMessage(
  code: string,
  retryCount: number,
  cancelled?: boolean,
  retryable?: boolean
): boolean {
  if (retryable === false) return false;
  if (cancelled) return false;
  if (retryCount >= RETRY_CONFIG.maxAttempts) return false;

  const category = getErrorCategory(code);
  return category === ErrorCategory.RATE_LIMIT || category === ErrorCategory.SYSTEM;
}

/**
 * Calculate time until next retry
 */
export function calculateTimeUntilRetry(error?: {
  timestamp: number;
  retryCount: number;
  retryable?: boolean;
}): string | null {
  if (
    !error ||
    error.retryable === false ||
    !error.timestamp ||
    error.retryCount >= RETRY_CONFIG.maxAttempts
  ) {
    return null;
  }

  // Calculate backoff time based on retry count
  const backoffSeconds = Math.min(
    RETRY_CONFIG.minBackoff *
      Math.pow(2, Math.min(error.retryCount - 1, RETRY_CONFIG.maxDoublings)),
    RETRY_CONFIG.maxBackoff
  );
  const nextRetryTime = error.timestamp + backoffSeconds * 1000;
  const now = Date.now();

  if (nextRetryTime <= now) {
    return null;
  }

  const timeUntilRetry = Math.floor((nextRetryTime - now) / 1000);
  const minutes = Math.floor(timeUntilRetry / 60);
  const seconds = timeUntilRetry % 60;

  return minutes > 0 ? `${minutes}m` : `${seconds}s`;
}
